'use client';

import Image from 'next/image';
import Link from 'next/link';

import { GTMEventAuth, useGTMPromotionBanner } from '@/fo/api/gtm';
import ArrowIcon from '@/fo/icons/base/icons/big-arrow.svg';
import { UiAdvertisingInformerBase } from '@/fo/ui/advertising/informer/base';
import { utilEventAuthModal } from '@/fo/util/event-auth-modal';

import { styles } from './UiKitBannerCashback.styles';

type NativeElement = React.HTMLAttributes<HTMLDivElement>;

export interface UiKitBannerCashbackProps {
  className?: string;
  title: React.ReactNode;
  text: React.ReactNode;
  desktopImage: string;
  mobileImage: string;
  alt?: string;
  link?: string;
  buttonText?: string;
  onClick?: NativeElement['onClick'];
  erid?: string;
  promotionName?: string;
  advertisingWarningText?: string;
  openAuthOnClick?: boolean;
  authReferral?: GTMEventAuth['referral'];
}

export function UiKitBannerCashback({
  className,
  title,
  text,
  desktopImage,
  mobileImage,
  alt = '',
  link,
  buttonText,
  erid,
  promotionName = '',
  advertisingWarningText,
  openAuthOnClick = false,
  authReferral = 'header',
  ...props
}: UiKitBannerCashbackProps) {
  const slots = styles();
  const { ref, trackSelectPromotion } = useGTMPromotionBanner({
    creativeName: desktopImage || mobileImage,
    promotionId: link || '',
    promotionName: promotionName || alt,
    source: 'static',
    index: 1
  });

  return (
    <div
      ref={ref}
      className={slots.base({ className })}
      {...props}
    >
      {erid && (
        <UiAdvertisingInformerBase
          erid={erid}
          advertisingWarningText={advertisingWarningText}
          className={slots.advertisingInformer()}
          classNameContent={slots.advertisingInformerContent()}
        />
      )}
      <picture>
        <source
          srcSet={mobileImage}
          media="(max-width: 639px)"
        />
        <Image
          alt={alt}
          src={desktopImage}
          width={1360}
          height={200}
          className={slots.image()}
        />
      </picture>
      <div className={slots.content()}>
        <div className={slots.body()}>
          <div className={slots.title()}>{title}</div>
          <p
            className={slots.text()}
            dangerouslySetInnerHTML={{ __html: text as string }}
          ></p>
          {buttonText && (
            <Link
              href={link || '/'}
              onClick={(event) => {
                if (openAuthOnClick) {
                  event.preventDefault();
                  utilEventAuthModal.open(
                    authReferral,
                    `${window.location.pathname}${window.location.search}${window.location.hash}`
                  );
                  return;
                }

                trackSelectPromotion();
              }}
            >
              <span className={slots.link()}>
                <span className={slots.linkText()}>{buttonText}</span>
                <span className={slots.linkIcon()}>
                  <ArrowIcon />
                </span>
              </span>
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
